<script lang="ts">
	import classNames from 'classnames';
	import type { Component } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { ChevronDownIcon } from '../../../../../assets/icons/ChevronDownIcon';
	import Button, { type ButtonProps } from '../../../../../components/Button/Button.svelte';
	import { Link } from '../../../../../components/Link';
	import LinkButton, {
		type LinkButtonProps
	} from '../../../../../components/LinkButton/LinkButton.svelte';
	import { Txt } from '../../../../../components/Txt';
	import { toKebabCase } from '../../../../../utils/toKebabCase';
	import type { SiteNavLink } from '../../../types';
	import { MenuItem } from '../../MenuItem';

	let focus = $state(false);

	let {
		selected = false,
		href,
		label = undefined,
		Icon = undefined,
		newTab = undefined,
		dataCy,
		items = undefined,
		buttonProps = undefined,
		linkButtonProps = undefined,
		children = undefined,
		custom = undefined,
		classes = {},
		onclick,
		...rest
	}: Props = $props();

	interface Props {
		selected?: boolean | undefined;
		buttonProps?: ButtonProps & { title: string; dataCy: string };
		linkButtonProps?: LinkButtonProps & { title: string; dataCy: string };
		children?: import('svelte').Snippet;
		// Rendered as-is in place of the default link/button, for entries whose
		// markup can't be expressed as a Link (e.g. a button with a hover card,
		// which can't sit inside the <a> that `children` is wrapped in).
		custom?: import('svelte').Snippet;
		href: string | undefined;
		label?: string | undefined;
		Icon?: Component | undefined;
		newTab?: boolean | undefined;
		dataCy: string;
		class?: string;
		items?: SiteNavLink['items'];
		onclick?: () => void;
		classes?: { link?: string; menu?: string };
	}
</script>

<div
	class={twMerge('relative h-full', rest.class)}
	role={items?.length ? 'combobox' : 'link'}
	onmouseenter={() => {
		if (!buttonProps && !linkButtonProps) {
			focus = true;
		}
	}}
	onmouseleave={() => {
		if (!buttonProps && !linkButtonProps) {
			focus = false;
		}
	}}
>
	<div class="relative h-full">
		{#if custom}
			<div class="flex h-full items-center">
				{@render custom()}
			</div>
		{:else if buttonProps}
			<div class="flex h-full items-center">
				<Button {...buttonProps}>
					{buttonProps.title}
				</Button>
			</div>
		{:else if linkButtonProps}
			<div class="flex h-full items-center">
				<LinkButton {...linkButtonProps}>
					{linkButtonProps.title}
				</LinkButton>
			</div>
		{:else}
			<Link
				{href}
				data-cy={`${dataCy}-${toKebabCase(label)}-link`}
				{onclick}
				aria-current={selected ? 'page' : undefined}
				class={classNames('flex h-full items-center', classes.link)}
				{...newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}}
			>
				{#if label}
					<Txt variant="label3">{label}</Txt>
					{#if items?.length}
						<ChevronDownIcon class="ml-1 h-3 w-3" />
					{/if}
				{/if}
				{#if Icon}
					<Icon class="h-5 w-5" />
				{/if}
				{#if children}
					{@render children?.()}
				{/if}
			</Link>
		{/if}

		{#if selected}
			<div class="absolute bottom-[-1px] h-[0.125rem] w-full bg-gray-900"></div>
		{/if}
	</div>
	{#if focus && items?.length}
		<div
			class="absolute right-0 top-[3.5rem] z-40 w-[13.75rem] max-h-[calc(100vh-200px)] overflow-scroll shadow-sm rounded no-scrollbar"
			role="combobox"
			aria-controls="releases"
			aria-expanded={focus}
			tabindex="0"
		>
			<div class={twMerge('bg-gray-0 px-2 py-3 shadow-2xl', classes?.menu)}>
				{#each items as { title, subtitle, src, href, Icon, classes, className, onClick, newTab }}
					<MenuItem
						{Icon}
						{title}
						{subtitle}
						{src}
						{href}
						{newTab}
						{classes}
						class={className}
						onClick={() => {
							focus = false;
							if (onClick) {
								onClick();
							}
						}}
					/>
				{/each}
			</div>
		</div>
	{/if}
</div>
